import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import { submitInventorizeLlmExtraction } from '@/features/inventorization/inventorize-llm-submit';
import { InventorizePhotoLlmClient } from '@/features/inventorization/inventorize-photo-llm-client';
import { type AiExtraction, CAPTURE_SESSION_STATUSES } from '@/shared/capture/capture-session';
import { AI_EXTRACTION_JOB_TYPES, AI_EXTRACTION_STATUSES } from '@/shared/ocr/ai-extraction';
import { createQueuedLlmParseJob, createSkippedLlmParseJob } from '@/shared/ocr/llm-parse-jobs';
import { messages } from './messages';

interface LlmParseState {
  error: string | null;
  isSubmitting: boolean;
}

export function useInventorizeLlmParse(sessionId: string) {
  const intl = useIntl();
  const { activeLocation } = useStartupSession();
  const services = useInventoryAppServices();
  const repositories = useMemo(
    () => ({
      captureSessions: services.captureSessions,
      photoBlobs: services.photoBlobs,
    }),
    [services.captureSessions, services.photoBlobs],
  );
  const llmClient = useMemo(() => new InventorizePhotoLlmClient(), []);
  const [state, setState] = useState<LlmParseState>({
    error: null,
    isSubmitting: false,
  });

  function queueLlmParse(photoIds: string[]) {
    return saveParseJob(createQueuedLlmParseJob, photoIds, intl.formatMessage(messages.llmQueueError));
  }

  async function submitPhotosForExtraction(photoIds: string[]) {
    setState({ error: null, isSubmitting: true });

    try {
      if (!activeLocation?.apiKey) {
        throw new Error(intl.formatMessage(messages.llmLocationApiKeyRequired));
      }

      const updated = await submitInventorizeLlmExtraction({
        llmClient,
        locationApiKey: activeLocation.apiKey,
        photoIds,
        repositories,
        sessionId,
      });
      setState({ error: null, isSubmitting: false });

      return updated;
    } catch (error) {
      const createdAt = new Date().toISOString();
      const job: AiExtraction = {
        completedAt: createdAt,
        createdAt,
        errorCode: 'LLM_EXTRACTION_FAILED',
        id: `llm-${uuidv4()}`,
        jobType: AI_EXTRACTION_JOB_TYPES.geminiExtraction,
        output: {
          mode: 'server_submit',
          photoIds,
        },
        sessionId,
        status: AI_EXTRACTION_STATUSES.failed,
      };

      await repositories.captureSessions.update(sessionId, currentSession => ({
        ...currentSession,
        aiExtractions: [...currentSession.aiExtractions, job],
        updatedAt: createdAt,
      }));
      setState({
        error: error instanceof Error ? error.message : intl.formatMessage(messages.llmSubmitError),
        isSubmitting: false,
      });

      return undefined;
    }
  }

  function skipLlmParse(photoIds: string[], reason = 'offline') {
    return saveParseJob(
      input =>
        createSkippedLlmParseJob({
          ...input,
          reason,
        }),
      photoIds,
      intl.formatMessage(messages.llmSkipError),
    );
  }

  async function saveParseJob(
    createJob: (input: {
      createdAt: string;
      id: string;
      photoIds: string[];
      sessionId: string;
    }) => ReturnType<typeof createQueuedLlmParseJob>,
    photoIds: string[],
    fallbackError: string,
  ) {
    setState({ error: null, isSubmitting: true });

    try {
      const createdAt = new Date().toISOString();
      const job = createJob({
        createdAt,
        id: `llm-${uuidv4()}`,
        photoIds,
        sessionId,
      });

      const updated = await repositories.captureSessions.update(sessionId, session => ({
        ...session,
        aiExtractions: [...session.aiExtractions, job],
        status:
          session.status === CAPTURE_SESSION_STATUSES.draft ? CAPTURE_SESSION_STATUSES.needsReview : session.status,
        updatedAt: createdAt,
      }));
      setState({ error: null, isSubmitting: false });

      return updated;
    } catch (error) {
      setState({
        error: error instanceof Error ? error.message : fallbackError,
        isSubmitting: false,
      });

      return undefined;
    }
  }

  return {
    ...state,
    queueLlmParse,
    skipLlmParse,
    submitPhotosForExtraction,
  };
}
